import React, { useState } from "react";
import { firebase } from "@/api/firebaseAdapter";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Calendar, Mail, Edit2, Lock, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ESTADOS = {
  borrador: { label: "Borrador", color: "bg-gray-500" },
  enviado_email: { label: "Enviado por Email", color: "bg-blue-500" },
  enviado_erp: { label: "Enviado a ERP", color: "bg-green-500" },
  aceptado: { label: "Aceptado", color: "bg-green-600" },
  rechazado: { label: "Rechazado", color: "bg-red-500" }
};

export default function PresupuestosList({ presupuestos, leadId, onCargarPresupuesto }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [presupuestoAEliminar, setPresupuestoAEliminar] = useState(null);

  const getIniciales = (email) => {
    if (!email) return null;
    const nombre = email.split('@')[0];
    return nombre.charAt(0).toUpperCase();
  };

  const desbloquearMutation = useMutation({
    mutationFn: ({ id }) => firebase.entities.Presupuesto.update(id, {
      estado: "borrador",
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['presupuestos', leadId] });
      if (onCargarPresupuesto) {
        onCargarPresupuesto(data);
      }
      toast({
        title: "Presupuesto desbloqueado",
        description: "Ahora puedes editarlo en la pestaña Presupuesto",
      });
    },
  });

  const eliminarMutation = useMutation({
    mutationFn: ({ id }) => firebase.entities.Presupuesto.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['presupuestos', leadId] });
      toast({
        title: "Presupuesto eliminado",
        description: "El presupuesto ha sido eliminado correctamente",
      });
    },
  });

  const handleDesbloquear = async (presupuesto) => {
    await desbloquearMutation.mutateAsync({ id: presupuesto.id });
  };

  const handleCargarBorrador = (presupuesto) => {
    console.log('handleCargarBorrador llamado con:', presupuesto);
    if (onCargarPresupuesto) {
      onCargarPresupuesto(presupuesto);
      toast({
        title: "Presupuesto cargado",
        description: "Ve a la pestaña Presupuesto para editarlo",
      });
    }
  };

  const handleEliminar = async () => {
    if (presupuestoAEliminar) {
      await eliminarMutation.mutateAsync({ id: presupuestoAEliminar.id });
      setPresupuestoAEliminar(null);
    }
  };

  const esBloqueado = (estado) => {
    return estado === 'enviado_email' || estado === 'enviado_erp' || estado === 'aceptado' || estado === 'rechazado';
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-gray-900 dark:text-white">
          <FileText className="w-5 h-5" />
          Historial de Presupuestos
        </CardTitle>
      </CardHeader>
      <CardContent>
        {presupuestos.length === 0 ? (
          <p className="text-gray-500 dark:text-gray-400 text-center py-8">Sin presupuestos generados</p>
        ) : (
          <div className="space-y-4">
            {presupuestos.map((presupuesto) => (
              <div key={presupuesto.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white">{presupuesto.numero_presupuesto}</h4>
                      {esBloqueado(presupuesto.estado) && (
                        <Lock className="w-4 h-4 text-gray-400" />
                      )}
                      {presupuesto.ultimo_usuario_edicion && (
                        <div className="w-6 h-6 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-semibold">
                          {getIniciales(presupuesto.ultimo_usuario_edicion)}
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      <Calendar className="w-3 h-3 inline mr-1" />
                      {format(new Date(presupuesto.created_date), "dd MMM yyyy, HH:mm", { locale: es })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${ESTADOS[presupuesto.estado]?.color} text-white`}>
                      {ESTADOS[presupuesto.estado]?.label}
                    </Badge>
                    {presupuesto.estado === 'borrador' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleCargarBorrador(presupuesto)}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                    ) : esBloqueado(presupuesto.estado) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDesbloquear(presupuesto)}
                        disabled={desbloquearMutation.isPending}
                      >
                        <Edit2 className="w-3 h-3 mr-1" />
                        Desbloquear
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setPresupuestoAEliminar(presupuesto)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 mb-3">
                  {presupuesto.lineas.map((linea, index) => (
                    <div key={index} className="flex justify-between items-center text-sm bg-gray-50 dark:bg-gray-800 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700 dark:text-gray-300">{linea.producto_nombre} x{linea.cantidad}</span>
                        {linea.usuario_edicion && (
                          <div className="w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-semibold">
                            {getIniciales(linea.usuario_edicion)}
                          </div>
                        )}
                      </div>
                      <span className="font-medium text-gray-900 dark:text-white">{linea.precio_venta.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-gray-200 dark:border-gray-700">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Total</span>
                  <span className="text-lg font-bold text-indigo-600 dark:text-indigo-400">
                    {presupuesto.total_presupuesto.toFixed(2)}€
                  </span>
                </div>

                {presupuesto.fecha_envio_email && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                    <Mail className="w-3 h-3" />
                    Enviado por email el {format(new Date(presupuesto.fecha_envio_email), "dd MMM yyyy", { locale: es })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <AlertDialog open={!!presupuestoAEliminar} onOpenChange={() => setPresupuestoAEliminar(null)}>
        <AlertDialogContent className="dark:bg-gray-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900 dark:text-white">¿Eliminar presupuesto?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-400">
              Esta acción no se puede deshacer. El presupuesto {presupuestoAEliminar?.numero_presupuesto} será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleEliminar} className="bg-red-600 hover:bg-red-700">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}